import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Switch,
  FormControlLabel,
  Slider,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Image as ImageIcon,
  Tag as TagIcon
} from '@mui/icons-material';
import WYSIWYGEditor from './WYSIWYGEditor';
import { FaultCategory, FaultTopic } from '../types/maintenanceBank';
import { FaultTopicService } from '../services/maintenanceBankService';
import { useAuth } from '../contexts/AuthContext';

interface AddTopicDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (topic: FaultTopic) => void;
  categories: FaultCategory[];
  selectedCategoryId?: string;
}

const AddTopicDialog: React.FC<AddTopicDialogProps> = ({
  open,
  onClose,
  onSuccess,
  categories,
  selectedCategoryId
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // بيانات الموضوع
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState(selectedCategoryId || '');
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [estimatedTime, setEstimatedTime] = useState(30);
  const [isPublished, setIsPublished] = useState(true); // منشور بشكل افتراضي

  // إعادة تعيين النموذج عند فتح الحوار
  useEffect(() => {
    if (open) {
      setTitle('');
      setDescription('');
      setContent('');
      setCategoryId(selectedCategoryId || '');
      setTags([]);
      setNewTag('');
      setDifficulty('medium');
      setEstimatedTime(30);
      setIsPublished(true); // منشور بشكل افتراضي
      setError(null);
    }
  }, [open, selectedCategoryId]);

  // إضافة علامة جديدة
  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  // حذف علامة
  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  // معالجة الضغط على Enter في حقل العلامات
  const handleTagKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddTag();
    }
  };

  // حفظ الموضوع
  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // التحقق من البيانات المطلوبة
      if (!title.trim()) {
        throw new Error('عنوان الموضوع مطلوب');
      }
      if (!description.trim()) {
        throw new Error('وصف الموضوع مطلوب');
      }
      if (!content.trim()) {
        throw new Error('محتوى الموضوع مطلوب');
      }
      if (!categoryId) {
        throw new Error('يجب اختيار فئة للموضوع');
      }
      if (!currentUser) {
        throw new Error('يجب تسجيل الدخول أولاً');
      }

      // إنشاء بيانات الموضوع
      const topicData = {
        categoryId,
        title: title.trim(),
        description: description.trim(),
        content: content.trim(),
        images: [], // سيتم استخراجها من المحتوى لاحقاً
        attachments: [],
        tags,
        difficulty,
        estimatedTime,
        isPublished,
        order: 0, // سيتم تحديدها من الخادم
        createdBy: currentUser.id,
        branchId: currentUser.branchId
      };

      // حفظ الموضوع
      const topicId = await FaultTopicService.addTopic(topicData);

      // جلب الموضوع المحفوظ
      const savedTopic = await FaultTopicService.getTopic(topicId);
      if (savedTopic) {
        onSuccess(savedTopic);
      }

      onClose();
    } catch (error: any) {
      console.error('خطأ في حفظ الموضوع:', error);
      setError(error.message || 'حدث خطأ أثناء حفظ الموضوع');
    } finally {
      setLoading(false);
    }
  };

  // تحديد لون مستوى الصعوبة
  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'easy': return '#4caf50';
      case 'medium': return '#ff9800';
      case 'hard': return '#f44336';
      default: return '#757575';
    }
  };

  // تحديد نص مستوى الصعوبة
  const getDifficultyText = (level: string) => {
    switch (level) {
      case 'easy': return 'سهل';
      case 'medium': return 'متوسط';
      case 'hard': return 'صعب';
      default: return 'غير محدد';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        borderBottom: '1px solid #e0e0e0'
      }}>
        <AddIcon />
        إضافة موضوع جديد
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* العمود الأيسر - المعلومات الأساسية */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 0 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                المعلومات الأساسية
              </Typography>

              <TextField
                fullWidth
                label="عنوان الموضوع"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ mb: 2 }}
                required
                placeholder="مثال: إصلاح شاشة الهاتف المكسورة"
              />

              <TextField
                fullWidth
                label="وصف مختصر"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                sx={{ mb: 2 }}
                required
                placeholder="وصف مختصر للمشكلة وطريقة الحل..."
              />

              <FormControl fullWidth sx={{ mb: 2 }} required>
                <InputLabel>الفئة</InputLabel>
                <Select
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                  label="الفئة"
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                مستوى الصعوبة
              </Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Select
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value as any)}
                  sx={{ 
                    '& .MuiSelect-select': { 
                      color: getDifficultyColor(difficulty),
                      fontWeight: 'bold'
                    }
                  }}
                >
                  <MenuItem value="easy" sx={{ color: '#4caf50' }}>🟢 سهل</MenuItem>
                  <MenuItem value="medium" sx={{ color: '#ff9800' }}>🟡 متوسط</MenuItem>
                  <MenuItem value="hard" sx={{ color: '#f44336' }}>🔴 صعب</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                الوقت المقدر: {estimatedTime} دقيقة
              </Typography>
              <Slider
                value={estimatedTime}
                onChange={(_, value) => setEstimatedTime(value as number)}
                min={5}
                max={180}
                step={5}
                marks={[
                  { value: 5, label: '5د' },
                  { value: 30, label: '30د' },
                  { value: 60, label: '1س' },
                  { value: 120, label: '2س' },
                  { value: 180, label: '3س' }
                ]}
                sx={{ mb: 2 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={isPublished}
                    onChange={(e) => setIsPublished(e.target.checked)}
                  />
                }
                label="نشر الموضوع فوراً"
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              {/* العلامات */}
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                <TagIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                العلامات
              </Typography>
              
              <TextField
                fullWidth
                label="إضافة علامة"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                sx={{ mb: 1 }}
                placeholder="اكتب علامة واضغط Enter"
                InputProps={{
                  endAdornment: (
                    <Button size="small" onClick={handleAddTag} disabled={!newTag.trim()}>
                      إضافة
                    </Button>
                  )
                }}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleRemoveTag(tag)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          {/* العمود الأيمن - المحرر */}
          <Grid item xs={12} md={8}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
              محتوى الموضوع
            </Typography>
            
            <WYSIWYGEditor
              value={content}
              onChange={setContent}
              placeholder="اكتب محتوى الموضوع هنا... يمكنك إضافة النصوص والصور والروابط"
              minHeight={400}
              maxHeight={500}
              disabled={loading}
            />

            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              💡 يمكنك رفع الصور مباشرة من شريط الأدوات أو سحبها وإفلاتها في المحرر
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e0e0e0' }}>
        <Button 
          onClick={onClose} 
          startIcon={<CancelIcon />}
          disabled={loading}
        >
          إلغاء
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading || !title.trim() || !description.trim() || !content.trim() || !categoryId}
        >
          {loading ? 'جاري الحفظ...' : 'حفظ الموضوع'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTopicDialog;
